<script>
import { mapGetters } from 'vuex'

import LoginFormInput from './LoginFormInput.vue'
import LoginFormAction from './LoginFormAction.vue'

import { normalizePhoneNumber } from '@/utils/phone-number'

export default {
  name: 'LoginForm',
  components: {
    LoginFormInput,
    LoginFormAction,
  },
  props: {
    submitting: {
      type: Boolean,
      default: false,
    },
    submittingGoogleSso: {
      type: Boolean,
      default: false,
    },
    redirectingToOnboarding: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      email: '',
      phoneNumber: '',
      password: '',
      countryCallingCode: '966',
      loginMethod: 'email',
      otpDialogVisible: false,
      verifyingPassword: false,
      errorMessages: {
        email: '',
        password: '',
        phoneNumber: '',
      },
    }
  },
  computed: {
    ...mapGetters('userProfile', {
      userPermissions: 'userPermissions',
    }),
    isUsingEmail() {
      return this.loginMethod === 'email'
    },
  },
  watch: {
    email: function() {
      this.$track.trackOnce('login_page_email_interacted')
    },
    phoneNumber: function() {
      this.$track.trackOnce('login_page_phone_number_interacted')
    },
    password: function() {
      this.$track.trackOnce('login_page_password_interacted')
    },
  },
  methods: {
    validateEmail() {
      this.errorMessages.email = ''

      if (!this.email) {
        this.errorMessages.email = this.$t('email_required')

        return false
      }

      if (!this.email.includes('@')) {
        this.errorMessages.email = this.$t('email_not_valid')

        return false
      }

      return true
    },
    validatePassword() {
      this.errorMessages.password = ''

      if (!this.password) {
        this.errorMessages.password = this.$t('password_required')

        return false
      }

      return true
    },
    validatePhoneNumber() {
      this.errorMessages.phoneNumber = ''

      if (!this.phoneNumber) {
        this.errorMessages.phoneNumber = this.$t('phone_number_required')

        return false
      }

      return true
    },
    validateForm() {
      return {
        email: this.validateEmail(),
        password: this.validatePassword(),
        phoneNumber: this.validatePhoneNumber(),
      }
    },
    validateEmailOrPhoneNumber() {
      if (this.isUsingEmail) {
        return this.validateEmail()
      }

      return this.validatePhoneNumber()
    },
    handleSubmitEmailAccount() {
      const isValidForm = this.validateForm()

      if (!isValidForm.email || !isValidForm.password) {
        return
      }

      this.$emit('login:email', {
        email: this.email,
        password: this.password,
      })
    },
    handleSubmitPhoneNumberAccount() {
      const isValidForm = this.validateForm()

      if (!isValidForm.phoneNumber || !isValidForm.password) {
        return
      }

      this.$emit('login:phone-number', {
        countryCallingCode: this.countryCallingCode,
        phoneNumber: this.phoneNumber,
        password: this.password,
      })
    },
    handleLogin(action) {
      this.phoneNumber = normalizePhoneNumber(
        this.phoneNumber,
        this.countryCallingCode
      )

      if (!this.verifyingPassword && this.validateEmailOrPhoneNumber()) {
        this.verifyingPassword = true

        return false
      }

      if (action === 'pressedEnter') {
        this.$track.event('login_page_login_enter')
      } else {
        this.$track.event('login_page_login_click')
      }

      if (this.isUsingEmail) {
        this.handleSubmitEmailAccount()

        return
      }

      this.handleSubmitPhoneNumberAccount()
    },
    handleGoogleSsoLogin() {
      this.$emit('login:sso', 'google')

      this.$track.event('login_page_sign_in_with_google_clicked')
    },
    handleForgotPassword() {
      this.$emit('click:forgot-password')

      this.$track.event('login_page_forget_password_clicked')
    },
    handleSignup() {
      this.$emit('click:signup')
    },
    handleSubmitForm(event) {
      // Cancel the event as stated by the standard.
      event.preventDefault()
    },
  },
}
</script>

<template>
  <!-- This form element helps browser to autocomplete the form fields -->
  <form novalidate class="tamara-login-form" @submit="handleSubmitForm">
    <div
      class="px-5 rounded-none tamara-login-form__body sm:rounded-md sm:p-10"
    >
      <div class="tamara-login-form__body__title">
        {{ $t('login_title') }}
      </div>
      <LoginFormInput
        :login-method.sync="loginMethod"
        :email.sync="email"
        :phone-number.sync="phoneNumber"
        :country-calling-code.sync="countryCallingCode"
        :password.sync="password"
        :submitting="submitting"
        :verifying-password="verifyingPassword"
        :error-messages="errorMessages"
        @blur:email="validateEmail"
        @blur:password="validatePassword"
        @blur:phone-number="validatePhoneNumber"
        @click:change="verifyingPassword = false"
        @click:forgot-password="handleForgotPassword"
        @enter:email="handleLogin('pressedEnter')"
        @enter:phone="handleLogin('pressedEnter')"
        @enter:password="handleLogin('pressedEnter')"
        @click:phone-numer-tab="
          $track.event('login_page_your_mobile_number_clicked')
        "
      />
      <LoginFormAction
        :submitting="submitting"
        :submitting-google-sso="submittingGoogleSso"
        :redirecting-to-onboarding="redirectingToOnboarding"
        @click:login="handleLogin('clickedButton')"
        @click:signup="handleSignup"
        @click:google-login="handleGoogleSsoLogin"
      />
    </div>
  </form>
</template>

<style lang="scss">
@import '@/views/login/styles/login.scss';
</style>
